import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatApiError } from "../lib/api";

const FIELDS = [
  "Real Estate", "Finance", "Government Policy", "Architecture",
  "Travel & Tourism", "Luxury Retail", "Marketing", "Fund Raising",
];

export default function Booking() {
  const [tab, setTab] = useState("free"); // free | premium
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("");

  const [freeForm, setFreeForm] = useState({ name: "", email: "", phone: "", field: FIELDS[0], preferred_date: "", message: "" });
  const [premiumForm, setPremiumForm] = useState({ name: "", email: "", phone: "", field: FIELDS[0], preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [freeSubmitted, setFreeSubmitted] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => {
      setPackages(data);
      setSelectedPkg(data?.[0]?.id || "");
    });
    if (params.get("cancelled")) {
      toast.error("Payment cancelled. You can try again when ready.");
    }
  }, [params]);

  const submitFree = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings/free", freeForm);
      setFreeSubmitted(true);
      toast.success("Request received. We will be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPremium = async (e) => {
    e.preventDefault();
    if (!selectedPkg) return;
    setSubmitting(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", {
        package_id: selectedPkg,
        ...premiumForm,
        origin_url,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <Navbar />
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16">
        <p className="overline">Engage the Practice</p>
        <h1 className="font-display font-light text-4xl md:text-6xl tracking-tighter mt-4 max-w-3xl">
          Two ways to <span className="italic text-[var(--pov-cyan)]">start moving.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-white/65">
          A complimentary inquiry to open the door, or a paid premium session when you're ready to
          move now. Either way, you'll hear back inside one business day — often sooner.
        </p>

        <div className="mt-10 inline-flex rounded-sm overflow-hidden border border-white/10" role="tablist">
          <button
            type="button"
            onClick={() => setTab("free")}
            data-testid="tab-free"
            className={`px-5 py-3 text-sm transition-colors ${tab === "free" ? "bg-white/8 text-white" : "text-white/55 hover:text-white"}`}
          >
            Free Consultation Request
          </button>
          <button
            type="button"
            onClick={() => setTab("premium")}
            data-testid="tab-premium"
            className={`px-5 py-3 text-sm transition-colors ${tab === "premium" ? "bg-[var(--pov-coral)]/15 text-[var(--pov-coral)]" : "text-white/55 hover:text-white"}`}
          >
            Premium Paid Booking
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {tab === "free" ? (
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-7 glass rounded-sm p-8 md:p-10">
              {freeSubmitted ? (
                <div className="text-center py-10" data-testid="free-submitted">
                  <CheckCircle2 className="w-12 h-12 text-[var(--pov-cyan)] mx-auto mb-4" />
                  <p className="font-display text-2xl">Thank you, {freeForm.name.split(" ")[0] || "friend"}.</p>
                  <p className="text-white/65 mt-3 max-w-md mx-auto">
                    Your request is in. Expect a personal reply inside one business day — often sooner.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitFree} className="space-y-5" data-testid="free-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input className="input" placeholder="Full name" required value={freeForm.name} onChange={e => setFreeForm({ ...freeForm, name: e.target.value })} data-testid="free-name" />
                    <input className="input" placeholder="Email" type="email" required value={freeForm.email} onChange={e => setFreeForm({ ...freeForm, email: e.target.value })} data-testid="free-email" />
                    <input className="input" placeholder="Phone (optional)" value={freeForm.phone} onChange={e => setFreeForm({ ...freeForm, phone: e.target.value })} data-testid="free-phone" />
                    <select className="input" value={freeForm.field} onChange={e => setFreeForm({ ...freeForm, field: e.target.value })} data-testid="free-field">
                      {FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <input className="input md:col-span-2" type="date" value={freeForm.preferred_date} onChange={e => setFreeForm({ ...freeForm, preferred_date: e.target.value })} data-testid="free-date" />
                  </div>
                  <textarea className="input min-h-[140px]" placeholder="A few sentences about the question you're sitting with…" required value={freeForm.message} onChange={e => setFreeForm({ ...freeForm, message: e.target.value })} data-testid="free-message" />
                  <button className="btn-primary" disabled={submitting} data-testid="free-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Send Request
                  </button>
                </form>
              )}
            </div>
            <aside className="col-span-12 lg:col-span-5 space-y-4">
              <div className="glass rounded-sm p-6">
                <p className="overline mb-2">What Happens Next</p>
                <ol className="space-y-3 text-sm text-white/70 list-decimal pl-4 marker:text-[var(--pov-cyan)]">
                  <li>You'll hear back within one business day — often sooner.</li>
                  <li>If we can help, we lock a 30-minute call this week.</li>
                  <li>From there: scope, retainer, or referral. We don't pitch slowly.</li>
                </ol>
              </div>
              <div className="glass rounded-sm p-6">
                <p className="overline mb-2">Looking for something more immediate?</p>
                <p className="text-sm text-white/65">Premium paid bookings lock a guaranteed time slot with the lead consultant this week.</p>
                <button onClick={() => setTab("premium")} className="btn-secondary mt-4 text-sm" data-testid="switch-to-premium">
                  Switch to Premium
                </button>
              </div>
            </aside>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5 space-y-3" data-testid="package-list">
              {packages.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPkg(p.id)}
                  className={`w-full text-left p-6 rounded-sm border transition-all duration-300 ${selectedPkg === p.id ? "border-[var(--pov-coral)] bg-[var(--pov-coral)]/8 shadow-[0_0_24px_rgba(255,107,74,0.18)]" : "border-white/8 glass hover:border-white/25"}`}
                  data-testid={`package-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="overline">{p.id.replace(/_/g, " ")}</p>
                      <p className="font-display text-xl mt-1">{p.name}</p>
                      <p className="text-sm text-white/60 mt-2">{p.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl">${p.amount.toFixed(0)}</p>
                      <p className="text-xs text-white/45 uppercase">{p.currency}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="col-span-12 lg:col-span-7 glass rounded-sm p-8 md:p-10">
              <form onSubmit={submitPremium} className="space-y-5" data-testid="premium-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input className="input" placeholder="Full name" required value={premiumForm.name} onChange={e => setPremiumForm({ ...premiumForm, name: e.target.value })} data-testid="premium-name" />
                  <input className="input" placeholder="Email" type="email" required value={premiumForm.email} onChange={e => setPremiumForm({ ...premiumForm, email: e.target.value })} data-testid="premium-email" />
                  <input className="input" placeholder="Phone (optional)" value={premiumForm.phone} onChange={e => setPremiumForm({ ...premiumForm, phone: e.target.value })} data-testid="premium-phone" />
                  <select className="input" value={premiumForm.field} onChange={e => setPremiumForm({ ...premiumForm, field: e.target.value })} data-testid="premium-field">
                    {FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <input className="input md:col-span-2" type="date" value={premiumForm.preferred_date} onChange={e => setPremiumForm({ ...premiumForm, preferred_date: e.target.value })} data-testid="premium-date" />
                </div>
                <textarea className="input min-h-[120px]" placeholder="Context for the conversation (optional but encouraged)…" value={premiumForm.message} onChange={e => setPremiumForm({ ...premiumForm, message: e.target.value })} data-testid="premium-message" />
                <div className="flex items-center gap-3 text-xs text-white/55">
                  <Clock className="w-4 h-4 text-[var(--pov-cyan)]" />
                  Secure checkout via Stripe. You will be redirected to complete payment.
                </div>
                <button className="btn-primary" disabled={submitting || !selectedPkg} data-testid="premium-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Continue to Secure Checkout
                </button>
              </form>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
